/**
 * useAnalysisQueryCache — React Query cache management for analysis results
 *
 * Extracted from useAnalyze.ts as part of E3 refactoring, S3.2 (#743).
 *
 * Provides two operations:
 * 1. cacheAnalysisResult() — Update analyze-drama query data + extend gcTime for anonymous
 * 2. invalidateDeviceQuota() — Invalidate quota cache after analysis (forces refetch)
 */

import { useQueryClient } from '@tanstack/react-query';
import type { ApiResponse, DramaAnalysisResponse } from '@dramadetector/api-client';
import { quotaKeys } from '../../../hooks/useQuota';
import { getDeviceId } from '@dramadetector/shared/device/storage';

/**
 * React Query cache operations for analysis results.
 *
 * @returns { cacheAnalysisResult, invalidateDeviceQuota }
 */
export function useAnalysisQueryCache() {
  const queryClient = useQueryClient();

  /**
   * Cache analysis result in React Query and extend gcTime for anonymous users.
   *
   * Anonymous analyses are NOT stored in DB, so cache eviction = data loss.
   * Extends gcTime to 1 hour (vs 10 min default) to prevent quota loss.
   *
   * @param response - The full analysis API response
   * @param message - The original message that was analyzed
   * @param context - The context type (friends, family, etc.)
   * @param isAnonymous - Whether the user is anonymous (drives gcTime extension)
   */
  function cacheAnalysisResult(
    response: ApiResponse<DramaAnalysisResponse>,
    message: string,
    context: string | undefined,
    isAnonymous: boolean
  ): void {
    if (!response.data) return;

    // Update query data with message + context augmentation
    queryClient.setQueryData(
      ['analyze-drama'],
      { ...response, data: { ...response.data, message, context } },
      {
        updatedAt: Date.now(),
      }
    );

    // Anonymous-specific: extend gcTime to prevent quota loss from cache eviction
    // Anonymous analyses are ephemeral (NOT stored in DB), so cache eviction = data loss
    if (isAnonymous) {
      queryClient.setQueryDefaults(['analyze-drama'], {
        gcTime: 60 * 60 * 1000, // 1 hour (vs 10 min default)
      });

      console.log('[useAnalysisQueryCache] Extended cache TTL for anonymous user:', {
        analysisId: response.data.id.substring(0, 8) + '...',
        gcTime: '1 hour',
        reason: 'quota_loss_prevention',
      });
    }
  }

  /**
   * Invalidate the device quota cache after an analysis completes.
   *
   * Why invalidate instead of setQueryData?
   * - Invalidate forces refetch, ensuring consistency
   * - Simpler than extracting anonymousQuota and manually updating cache
   *
   * Flow:
   * 1. Analysis completes, server deducts quota
   * 2. Client receives response
   * 3. Invalidate quota cache
   * 4. useQuota refetches from get_anonymous_quota RPC
   * 5. UI shows updated quota (e.g., "9 of 10 remaining")
   */
  function invalidateDeviceQuota(): void {
    getDeviceId()
      .then((deviceId) => {
        queryClient.invalidateQueries({
          queryKey: quotaKeys.usage('device', deviceId),
        });

        console.log('[useAnalysisQueryCache] Invalidated quota cache for anonymous user:', {
          deviceId: deviceId.substring(0, 12) + '...',
          reason: 'post_analysis_update',
        });
      })
      .catch((error) => {
        console.error('[useAnalysisQueryCache] Failed to invalidate quota cache:', error);
      });
  }

  return { cacheAnalysisResult, invalidateDeviceQuota };
}
